import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Box,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { sendSupportRequest } from 'services/supportService';
import { DeleteOutlined, CloudUploadOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';

const SupportModal = ({ open, handleClose }) => {
  const [subject, setSubject] = useState('Support:');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ticketInfo, setTicketInfo] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const dialogContentRef = useRef(null);
  const theme = useTheme();

  const handleFileChange = (event) => {
    setAttachments((prev) => [...prev, ...Array.from(event.target.files)]);
  };

  const handleRemoveFile = (fileName) => {
    setAttachments(attachments.filter((file) => file.name !== fileName));
  };

  const handleAttachmentClick = () => {
    fileInputRef.current.click();
  };

  const resetState = () => {
    setSubject('Support:');
    setBody('');
    setAttachments([]);
    setIsDragging(false);
    setIsLoading(false);
    setTicketInfo(null);
    setError(null);
  };

  const takeScreenshot = async () => {
    // Hide the modal and backdrop temporarily
    const modal = document.querySelector('.MuiDialog-root');
    const backdrop = document.querySelector('.MuiBackdrop-root');
    if (modal) modal.style.display = 'none';
    if (backdrop) backdrop.style.display = 'none';

    try {
      // Take screenshot of the main content
      const canvas = await html2canvas(document.body, {
        ignoreElements: (element) => {
          // Ignore the modal and its backdrop
          return element.classList.contains('MuiDialog-root') || element.classList.contains('MuiBackdrop-root');
        },
        useCORS: true,
        scale: 2
      });

      // Convert to blob
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'autoscreenshot.png', { type: 'image/png' });

      // Add to attachments
      setAttachments((prev) => [...prev, file]);
      return file;
    } finally {
      // Restore modal and backdrop
      if (modal) modal.style.display = '';
      if (backdrop) backdrop.style.display = '';
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Take screenshot before sending
      const screenshotFile = await takeScreenshot();

      // Create a new array with all attachments including the screenshot
      const allAttachments = [...attachments, screenshotFile];

      // Send the request with all attachments including the screenshot
      const response = await sendSupportRequest({ subject, body, attachments: allAttachments });
      setTicketInfo(response);
    } catch (error) {
      console.error('Failed to send support request', error);
      setError(error.response?.data?.error || 'Failed to send support request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    handleClose();
    resetState();
  };

  const handlePaste = useCallback((event) => {
    const items = event.clipboardData.items;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        files.push(items[i].getAsFile());
      }
    }
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      setIsDragging(false);
    };
    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        setAttachments((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
      }
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('paste', handlePaste);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('paste', handlePaste);
    };
  }, [open, handlePaste]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) {
      return `${mb.toFixed(2)} MB`;
    }
    const kb = bytes / 1024;
    return `${kb.toFixed(2)} KB`;
  };

  const dropzoneStyles = {
    border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.grey[300]}`,
    borderRadius: 1,
    p: 3,
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.light
    }
  };

  const renderContent = () => {
    if (ticketInfo) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Support Request Sent Successfully
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your ticket has been created and we&apos;ll get back to you soon.
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Ticket Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ticket Number: {ticketInfo.ticket_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status: {ticketInfo.status}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(ticketInfo.created_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <DialogContentText sx={{ mb: 2 }}>
          You can reach us at <a href="mailto:info@askpot.com">info@askpot.com</a> or fill out the form below.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              margin="dense"
              id="subject"
              label="Subject"
              type="text"
              fullWidth
              variant="outlined"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                position: 'relative'
              }}
            >
              <TextField
                margin="dense"
                id="body"
                label="Message"
                type="text"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your message or drag files here"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box onClick={handleAttachmentClick} sx={dropzoneStyles}>
              <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileChange} />
              <CloudUploadOutlined style={{ fontSize: '48px', color: theme.palette.grey[500] }} />
              <Typography variant="h6" color="textSecondary">
                {isDragging ? 'Drop files here' : 'Drag and drop files here'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                or click to select files. You can also paste images from your clipboard.
              </Typography>
            </Box>
          </Grid>
          {attachments.length > 0 && (
            <Grid item xs={12}>
              <List dense>
                {attachments.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFile(file.name)}>
                        <DeleteOutlined />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleCancel} aria-labelledby="support-modal-title" fullWidth maxWidth="sm">
      <DialogTitle id="support-modal-title">{ticketInfo ? 'Support Request Sent' : 'Contact Support'}</DialogTitle>
      <DialogContent
        ref={dialogContentRef}
        sx={{
          border: isDragging ? `2px dashed ${theme.palette.primary.main}` : '2px solid transparent',
          transition: 'border 0.2s ease-in-out'
        }}
      >
        {renderContent()}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {ticketInfo ? (
          <Button onClick={handleCancel} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isLoading ? 'Sending...' : 'Send'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SupportModal;
